import { AxiosError } from 'axios'
import { API, unwrapData } from '../axios-instance'
import {
  BatchActionsConfig,
  BatchConfig,
  BatchRouteActions,
  BatchStepEnum,
  BatchingRoutes,
} from './batch.types'
import { BatchNotFoundError } from './errors'

type PaginationPreviewBody = {
  page: number
  pageSize: number
}

const handleBatchErrors = (error: AxiosError) => {
  if (error.response?.status === 404) {
    throw new BatchNotFoundError()
  }
  throw error
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/**
 * True when `body` is only paginated list preview input (`page` + `pageSize`, both finite numbers).
 *
 * Used by `createBatchingRoutes.preview` to choose the HTTP shape:
 * - **GET** with `page` / `pageSize` as query params (legacy table batches, e.g. bulk complete orders)
 * - **POST** with `?preview=true` when the body includes any other fields (e.g. bulk send award preview)
 *
 * Callers that only need a paged item list should pass exactly `{ page, pageSize }` and no extra keys.
 */
export const isPaginationPreviewBody = (body: unknown): body is PaginationPreviewBody => {
  if (!isRecord(body)) {
    return false
  }
  const keys = Object.keys(body)
  if (keys.length !== 2 || !keys.includes('page') || !keys.includes('pageSize')) {
    return false
  }
  const { page, pageSize } = body
  return (
    typeof page === 'number' &&
    Number.isFinite(page) &&
    typeof pageSize === 'number' &&
    Number.isFinite(pageSize)
  )
}

export const createBatchingRoutes = <Actions extends Partial<BatchRouteActions>>(
  config: BatchConfig,
): BatchingRoutes<Actions> => {
  type ResolvedActions = BatchActionsConfig<Actions>
  const additionalPath = (batchStep: BatchStepEnum) => {
    return config[batchStep]?.path ? `/${config[batchStep]?.path}` : ''
  }

  return {
    init: async (
      body?: ResolvedActions['init']['body'],
      params?: ResolvedActions['init']['params'],
    ) => {
      return await API.post<ResolvedActions['init']['result']>(
        `${config.baseUrl}${additionalPath(BatchStepEnum.PREVIEW)}`,
        body,
        {
          params,
          headers: config[BatchStepEnum.INITIATE]?.headers,
        },
      )
        .then(unwrapData)
        .catch(handleBatchErrors)
    },
    update: async (
      batchId: string,
      body?: ResolvedActions['update']['body'],
      params?: ResolvedActions['update']['params'],
    ) => {
      const url = `${config.baseUrl}/${batchId}${additionalPath(BatchStepEnum.UPDATE)}`
      return await API.post<ResolvedActions['update']['result']>(url, body, {
        params,
        headers: config[BatchStepEnum.UPDATE]?.headers,
      })
        .then(unwrapData)
        .catch(handleBatchErrors)
    },
    preview: async (batchId: string, body?: ResolvedActions['preview']['body']) => {
      const url = `${config.baseUrl}/${batchId}${additionalPath(BatchStepEnum.PREVIEW)}`
      if (body && isPaginationPreviewBody(body)) {
        return await API.get<ResolvedActions['preview']['result']>(url, {
          headers: config[BatchStepEnum.PREVIEW]?.headers,
          params: body,
        })
          .then(unwrapData)
          .catch(handleBatchErrors)
      }
      if (body) {
        return await API.post<ResolvedActions['preview']['result']>(url, body, {
          headers: config[BatchStepEnum.PREVIEW]?.headers,
          params: {
            preview: true,
          },
        })
          .then(unwrapData)
          .catch(handleBatchErrors)
      }
      return await API.get<ResolvedActions['preview']['result']>(url, {
        headers: config[BatchStepEnum.PREVIEW]?.headers,
      })
        .then(unwrapData)
        .catch(handleBatchErrors)
    },
    execute: async (batchId: string, body?: ResolvedActions['execute']['body']) =>
      API.post<ResolvedActions['execute']['result']>(
        `${config.baseUrl}/${batchId}${additionalPath(BatchStepEnum.EXECUTE)}`,
        body,
      )
        .then(unwrapData)
        .catch(handleBatchErrors),
  }
}
